import React from "react";
import { AlertTriangle } from "lucide-react";
import { MIN_QUESTIONS_PER_COMPETENCY } from "./QuestionEditor";

const blockCls =
  "rounded-2xl border border-gray-300 dark:border-white/10 bg-bgSecondary dark:glass-effect p-4 space-y-3";
const headingCls =
  "text-xs font-semibold uppercase tracking-wide dark:text-gray-400 text-textSecondary";

const formatPerQuestion = (seconds) => {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (!m) return `${s}s`;
  if (!s) return `${m}m`;
  return `${m}m ${s}s`;
};

/**
 * Right rail, same structural role as MCQ's BuilderSidebar. One deliberate
 * difference: the Summary card shows per-competency question counts instead of a
 * pass-threshold stat, since that's the actual save-blocking rule here (>=3
 * questions per assessed competency, >=2 competencies — proposal §1).
 */
const BuilderSidebar = ({
  questions = [],
  competencies = [],
  perCompetency = {},
  errors = {},
  config = {},
  onJumpTo,
  expandedId,
  saveLabel,
  saveBlockedReason,
  isSaving,
  isDirty,
  onSave,
  showReviewGate = false,
  reviewAcknowledged = false,
  onReviewAcknowledgedChange,
}) => {
  const count = questions.length;
  const timeLimit = config.time_limit_minutes || 0;
  const problemCount = questions.filter((q) => errors[q.id]).length;
  const perQuestion = count && timeLimit ? (timeLimit * 60) / count : 0;

  const competencyName = (id) => competencies.find((c) => c.id === id)?.name || "Unassigned";

  return (
    <div className="w-full lg:w-72 shrink-0 flex flex-col gap-4">
      <div className="lg:flex-1 lg:pb-2 lg:mt-8">
        <div className="space-y-4 lg:sticky lg:top-4">
          {count > 0 && (
            <div className={blockCls}>
              <div className="flex items-center justify-between gap-2">
                <h3 className={headingCls}>Navigator</h3>
                {problemCount > 0 && (
                  <span className="flex items-center gap-1 text-[11px] font-medium text-red-400">
                    <AlertTriangle size={11} />
                    {problemCount} to fix
                  </span>
                )}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {questions.map((q, i) => {
                  const isActive = q.id === expandedId;
                  const hasError = !!errors[q.id];
                  const summary = q.scenario_text || "Untitled question";
                  return (
                    <button
                      key={q.id}
                      onClick={() => onJumpTo(q.id)}
                      title={hasError ? `${summary} — ${errors[q.id]}` : summary}
                      className={`aspect-square rounded-lg text-xs font-medium flex items-center justify-center transition-colors ${
                        hasError
                          ? isActive
                            ? "border border-red-500 bg-red-500 text-white"
                            : "border border-red-400 bg-red-500/15 text-red-400"
                          : isActive
                          ? "knockout-tile"
                          : "gradient-ring always border border-gray-300 dark:border-white/15 dark:text-gray-400 text-textSecondary"
                      }`}
                    >
                      {isActive && !hasError ? (
                        <span className="knockout-tile-text">{i + 1}</span>
                      ) : (
                        i + 1
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {count > 0 && (
            <div className={blockCls}>
              <h3 className={headingCls}>Summary</h3>
              <dl className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <dt className="dark:text-gray-400 text-textSecondary">Questions</dt>
                  <dd className="dark:text-white text-textPrimary font-medium">{count}</dd>
                </div>
                {timeLimit > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <dt className="dark:text-gray-400 text-textSecondary">Time limit</dt>
                      <dd className="dark:text-white text-textPrimary font-medium">
                        {timeLimit} min
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="dark:text-gray-400 text-textSecondary">Time per Q (avg)</dt>
                      <dd className="dark:text-white text-textPrimary font-medium">
                        ≈{formatPerQuestion(perQuestion)}
                      </dd>
                    </div>
                  </>
                )}
                {Object.keys(perCompetency).length > 0 && (
                  <div className="pt-2 border-t border-white/10 space-y-1.5">
                    <dt className="dark:text-gray-400 text-textSecondary">Per competency</dt>
                    {Object.entries(perCompetency).map(([id, n]) => {
                      const firstQuestion = questions.find((q) => q.competency_id === id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => firstQuestion && onJumpTo(firstQuestion.id)}
                          title="Jump to these questions"
                          className="w-full flex items-center justify-between text-left hover:text-textPrimary dark:hover:text-white transition-colors"
                        >
                          <dd className="dark:text-gray-300 text-textSecondary">{competencyName(id)}</dd>
                          <dd className={`font-medium ${n < MIN_QUESTIONS_PER_COMPETENCY ? "text-amber-400" : "dark:text-white text-textPrimary"}`}>
                            {n} question{n === 1 ? "" : "s"}{n < MIN_QUESTIONS_PER_COMPETENCY ? ` (needs ${MIN_QUESTIONS_PER_COMPETENCY})` : ""}
                          </dd>
                        </button>
                      );
                    })}
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 flex flex-col items-center gap-1.5">
        {/* Only shown for AI-generated drafts — the recruiter must consciously
            confirm before an AI-drafted ranking can reach a candidate. Local-only
            state in the parent, never saved to the draft (behavioral-test-phase2-
            ai-guided-proposal.md's review-gate design). */}
        {showReviewGate && (
          <label className="flex items-start gap-2 text-left w-full max-w-[16rem] cursor-pointer mb-1">
            <input
              type="checkbox"
              checked={reviewAcknowledged}
              onChange={(e) => onReviewAcknowledgedChange?.(e.target.checked)}
              className="mt-0.5 shrink-0 accent-blue-500"
            />
            <span className="text-xs dark:text-gray-400 text-textSecondary leading-relaxed">
I have reviewed the questions, scenarios, and rankings provided below.            </span>
          </label>
        )}
        {/* The title tooltip below never fires on touch, so a mobile recruiter
            sees only a greyed-out button with no explanation — this text is the
            same reason, just always visible instead of hover-only. */}
        {saveBlockedReason && (
          <p className="text-xs text-amber-400 text-center max-w-[16rem]">
            {saveBlockedReason}
          </p>
        )}
        <span className="inline-flex" title={saveBlockedReason || undefined}>
          <button
            onClick={onSave}
            disabled={isSaving || !isDirty || !!saveBlockedReason}
            className="button disabled:opacity-50"
          >
            {isSaving ? "Saving…" : saveLabel}
          </button>
        </span>
      </div>
    </div>
  );
};

export default BuilderSidebar;
